import React, { useState, useContext, useEffect } from 'react';
import DateFnsUtils from "@date-io/date-fns";
import { Paper, Grid, createMuiTheme } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/styles";
import { makeStyles } from "@material-ui/core/styles";
import createBreakpoints from "@material-ui/core/styles/createBreakpoints";
import { DatePicker, MuiPickersUtilsProvider, } from '@material-ui/pickers';
import EligibilityTimeList from "../EligibilityTimeList/EligibilityTimeList";
import EligibilityStaticDateFilter from './EligibilityStaticDateFilter';
import { COORDINATES_FIELD, HOME_DETAILS_SQFT, LANGUAGE_TEXT } from "../../../../constants";
import withErrorHandler from "../../../../hoc/withErrorHandler/withErrorHandler";
import { EWContext } from "../../../../context/EWContext";
import { getContactError } from "../../../../utils/utils";
import { getRegionId, getJobTemplateData, getAvailability, getAvailableDays } from "../../../../services/services";
import { es, enUS } from "date-fns/locale";
import { DateTime } from "luxon";
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import moment from "moment";


function EligibilityStaticDate({ question, handlerInputChange, value, productType, zipCode }) {
  const [accountState, setAccountState] = useContext(EWContext);
  const [disableDatePicker, setDisableDatePicker] = useState(false);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [loadingMonthAvailability, setLoadingMonthAvailability] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [noAvailableMonth, setNoAvailableMonth] = useState(false);
  const [timePeriod, setTimePeriod] = useState();
  const [disableFilters, setDisableFilters] = useState(false);

  const {
    skeduloJobData,
    baseUrl,
    customer,
    customErrors,
    skeduloInfo,
    appointmentZip,
    productInfo,
    latitude,
    longitude,
    apptMaxTravelTime,
    apptIgnoreTravelTime,
    apptCustomTimeslotsIsEnable,
    apptSuggestedGap,
    apptResultMode,
    storeContact,
    appReference,
    apptHomeSqft,
    largeHomeSqft,
    largeHomeDuration,
    theme
  } = accountState;

  const { regionId, jobDuration, jobTagId, timezone } = skeduloJobData;
  const {
    programName,
    maxTravelTime,
    ignoreTravelTime,
    suggestedGap,
    resultMode,
    storeTimezone,
    largeHome,
    availableDaysEnable,
    minimumDaysToSchedule
  } = skeduloInfo;

  const breakpoints = createBreakpoints({});

  const materialTheme = createMuiTheme({
    overrides: {
        MuiGrid: {
            container: {
                marginBottom: 20,
                justifyContent: "space-between",
            },
            item: {
                [breakpoints.up("md")]: {
                    flexBasis: "40% !important",
                },
            },
        },
        MuiPickersStaticWrapper: {
            staticWrapperRoot: {
                boxShadow: `0px 0px 10px ${theme.shadowColor}`,
                border: `1px solid ${theme.shadowColor}`,
                borderRadius: 10,
                width: "fit-content",
            },
        },
        MuiTypography: {
            body1: {
                color: theme.textColorPrimary,
                fontFamily: "Avenir, sans-serif",
                fontSize: 20,
                fontWeight: 900,
            },
        },
        MuiPickersCalendarHeader: {
            switchHeader: {
                backgroundColor: "white",
                color: theme.primaryColorLight,
            },
            transitionContainer: {
                height: 30,
            },
            dayLabel: {
                font: "normal normal 300 13px/19px Avenir, sans-serif",
                color: theme.textColorSecondary,
                textTransform: "uppercase",
            },
        },
        MuiSvgIcon: {
            root: {
                width: "2em",
                height: "2em",
            },
        },
        MuiCircularProgress: {
          colorPrimary: {
            color: theme.primaryColor
          }
        }
    },
});

const styles = makeStyles(() => ({
    gridDay: {
        alignItems: "center",
        borderRadius: 0,
        boxShadow: "none",
        display: "flex",
        font: "normal normal bold 14px/21px Avenir, sans-serif",
        height: "35px",
        justifyContent: "center",
        margin: "3px",
        padding: "1px",
        width: "35px",
    },
    notInThisMonthDayPaper: {
        visibility: "hidden",
    },
    disabledDay: {
        color: theme.disabledControl,
        cursor: "default",
    },
    normalDayPaper: {
        color: theme.textColorPrimary,
        cursor: "pointer",
    },
    selectedDayPaper: {
        backgroundColor: theme.primaryColor,
        color: theme.lighterColor,
        cursor: "pointer",
    },
    todayPaper: {
        backgroundColor: theme.lighterColor,
        color: theme.textColorPrimary,
        cursor: "pointer",
    },
}));

  const classes = styles();
  const newZipCode = appointmentZip || zipCode;
  const newProductType = productInfo?.appointmentJobType || productType;
  const skeduloTagName = `${programName} ${newProductType}`;
  const addressCoordinates = accountState.answers.validate?.find(answer => COORDINATES_FIELD.includes(answer.id)).extraValues || { latitude, longitude };
  const newLatitude = appReference ? (addressCoordinates?.latitude || 0) + 1e-2 : addressCoordinates?.latitude || "";
  const newLongitude = appReference ? (addressCoordinates?.longitude || 0) + 1e-2 : addressCoordinates?.longitude || "";
  const homeSqft = accountState.answers.homeDetails?.find(answer => HOME_DETAILS_SQFT.includes(answer.id)).value || apptHomeSqft;
  const largeHomeSqftValue = largeHome?.sqft || largeHomeSqft;
  const largeHomeDurationValue = largeHome?.duration || largeHomeDuration;
  const newIgnoreTravelTime = !appReference ? ignoreTravelTime : apptIgnoreTravelTime || false;
  const newCustomTimeSlotsIsEnable = !appReference ? skeduloInfo?.customTimeSlots?.isEnable : apptCustomTimeslotsIsEnable || false;

  const [errorInForm, setErrorInForm] = useState({
    message: customErrors.genericError,
    genericMessage: customErrors.contactError,
    labelButton: "Back",
    error: false,
    goStep: 0
  });

  // Using store time zone to create the DatePicker
  const today = DateTime.now().setZone(storeTimezone).startOf("day");
  const twoDaysFromNow = today.plus({ days: typeof minimumDaysToSchedule !== "undefined" ? minimumDaysToSchedule : 2 });
  const startDate = twoDaysFromNow;
  const endDate = twoDaysFromNow.endOf("month").startOf("day");
  const [selectedDate, setSelectedDate] = useState(value || twoDaysFromNow);

  const displayError = (error, secondaryMessage) => {
    const errorMessage = error?.message || error;
    setErrorInForm(prev => {
      return {
        ...prev,
        ...{
          message: errorMessage || customErrors.genericError,
          genericMessage: secondaryMessage || '',
          error: true,
          goStep: 0
        }
      }
    })
  };

  function disableDates(date) {
    const initialDate = new Date(date);
    const currentDate = DateTime.fromJSDate(initialDate).toISODate();
    return !availableDates.includes(currentDate);
  }

  function obtainAvailableDates(startDate, endDate, timePeriod = "all") {
    setAvailableDates([]);
    setLoadingAvailability(true);
    setLoadingMonthAvailability(true);
    setDisableFilters(true);

    const requestBody = {
      availableDays: {
        startDate: startDate.toISODate(),
        endDate: endDate.toISODate(),
        timePeriod,
        zipCode: newZipCode,
        duration: jobDuration,
        latitude: newLatitude,
        longitude: newLongitude,
        regionId,
        jobType: newProductType,
        jobTags: [jobTagId],
        maxTravelTime: maxTravelTime || apptMaxTravelTime || 0,
        ignoreTravelTime: newIgnoreTravelTime,
      },
    };

    getAvailableDays(baseUrl, customer, requestBody)
      .then((availabilityResponse) => {
        if (availabilityResponse.length) {
          const datesAvailability = availabilityResponse[0].available || [];
          let selectedDay = "";
          if (datesAvailability.length > 0) {
            selectedDay = DateTime.fromISO(datesAvailability[0]);
            setNoAvailableMonth(false);
          } else {
            selectedDay = startDate;
            setNoAvailableMonth(true);
            setAvailableDates([]);
            setAvailableTimes([]);
            setLoadingAvailability(false);
            setLoadingMonthAvailability(false);
            setDisableFilters(false);
          }
          setSelectedDate(selectedDay);
          setAvailableDates(datesAvailability);
          setLoadingMonthAvailability(false);
        } else {
          displayError(customErrors.customEligibilityQuestionsError ? customErrors.customEligibilityQuestionsError : getContactError(storeContact));
          console.error(`There's a problem fetching available days -> Available days service response: ${JSON.stringify(availabilityResponse)}`);
        }
      })
      .catch((error) => {
        displayError(customErrors.customEligibilityQuestionsError ? customErrors.customEligibilityQuestionsError : getContactError(storeContact));
        console.error(error);
      });
  }

  function handleMonthChange(month) {
    const startDateMonth = DateTime.fromJSDate(month).startOf("month").startOf("day");
    const endDateMonth = DateTime.fromJSDate(month).endOf("month").startOf("day");
    const initialDate = startDateMonth < startDate ? startDate : startDateMonth;
    if (availableDaysEnable) {
      obtainAvailableDates(initialDate, endDateMonth, timePeriod);
    } else {
      setSelectedDate(initialDate);
      setLoadingMonthAvailability(false);
    }
  }

  function handleFilterChange(value) {
    setTimePeriod(value);
    const startDateMonth = selectedDate.startOf("month").startOf("day");
    const endDateMonth = selectedDate.endOf("month").startOf("day");
    const initialDate = startDateMonth < startDate ? startDate : startDateMonth;
    if (availableDaysEnable) {
      obtainAvailableDates(initialDate, endDateMonth, value);
    } else {
      setLoadingMonthAvailability(false);
    }
  }

  function getDayElement(day, selectedDate, isInCurrentMonth, dayComponent) {
    const newDay = DateTime.fromJSDate(day);
    const newSelectedDate = DateTime.fromJSDate(selectedDate);
    const isSelected = newDay.day === newSelectedDate.day;
    const isToday = newDay.day === today.day && newDay.month === today.month;
    const isBeforeTwoDaysFromNow = newDay.day < twoDaysFromNow.day && newDay.month === twoDaysFromNow.month;

    let dateTile
    if (isInCurrentMonth) {
      if (isBeforeTwoDaysFromNow || dayComponent.props.disabled) {
        dateTile = (
          <Paper className={`${classes.gridDay} ${classes.disabledDay}`}>
            <Grid item> {newDay.day}</Grid>
          </Paper>)
      } else {
        dateTile = (
          <Paper className={`${classes.gridDay} ${isSelected ? classes.selectedDayPaper : isToday ? classes.todayPaper : classes.normalDayPaper}`}>
            <Grid item> {newDay.day}</Grid>
          </Paper>)
      }
    } else {
      dateTile = (<Paper className={`${classes.gridDay} ${classes.notInThisMonthDayPaper}`}>
        <Grid item>
          {newDay.day}
        </Grid>
      </Paper>)
    }
    return dateTile;
  }

  // Set error message to display
  useEffect(() => {
    if (errorInForm.error) {
      setAccountState((prevAccountState) => ({
        ...prevAccountState,
        ...{
          submitAttempts: prevAccountState.submitAttempts + 1,
          eligibilityError: errorInForm,
        },
      }));
    }
  }, [errorInForm, setAccountState]);

  // Get Region Skedulo service call
  useEffect(() => {
    getRegionId(baseUrl, customer, newZipCode).then(regionResponse => {
      if (regionResponse.length) {
        const regionId = regionResponse[0].regionId;
        const timezone = regionResponse[0].timezone;

        if (regionId && timezone) {
          setAccountState((prevAccountState) => ({
            ...prevAccountState,
            skeduloJobData: {
              ...prevAccountState.skeduloJobData,
              regionId,
              timezone,
            },
          }));
        } else {
          if (!regionId) {
            displayError(customErrors.customEligibilityQuestionsError ? customErrors.customEligibilityQuestionsError : getContactError(storeContact));
            console.error(`There's a problem fetching the region ID -> region Id service response: ${JSON.stringify(regionResponse)}`);
          } else if (!timezone) {
            displayError(customErrors.customEligibilityQuestionsError ? customErrors.customEligibilityQuestionsError : getContactError(storeContact));
            console.error(`There's a problem fetching the timezone -> region Id service response: ${JSON.stringify(regionResponse)}`);
          };
        }
      }
      else {
        displayError(customErrors.customEligibilityQuestionsError ? customErrors.customEligibilityQuestionsError : getContactError(storeContact));
      }
    }).catch(
      error => {
        displayError(customErrors.customEligibilityQuestionsError ? customErrors.customEligibilityQuestionsError : getContactError(storeContact));
        console.error(error);
      }
    );
    // eslint-disable-next-line
  }, []);

  // Get Templates Skedulo service call
  useEffect(() => {
    getJobTemplateData(baseUrl, customer, programName, newProductType, homeSqft, largeHomeSqftValue, largeHomeDurationValue).then(templateResponse => {
      if (templateResponse.length) {
        const jobDuration = templateResponse[0].data.find(item => item.field === 'Duration').value;
        const jobTagId = JSON.parse(templateResponse[0].data.find(item => item.field === 'ResourceRequirements').value).requirements[0].tags[0].tagId;

        if (jobDuration && jobTagId) {
          setAccountState((prevAccountState) => ({
            ...prevAccountState,
            skeduloJobData: {
              ...prevAccountState.skeduloJobData,
              jobDuration,
              jobTagId,
            },
          }));
        } else {
          if (!jobDuration) {
            displayError(customErrors.customEligibilityQuestionsError ? customErrors.customEligibilityQuestionsError : getContactError(storeContact));
            console.error(`There's a problem fetching the Job Duration -> Template service response: ${JSON.stringify(templateResponse)}`);
          } else if (!jobTagId) {
            displayError(customErrors.customEligibilityQuestionsError ? customErrors.customEligibilityQuestionsError : getContactError(storeContact));
            console.error(`There's a problem fetching the Job Tag ID -> Template service response: ${JSON.stringify(templateResponse)}`);
          };
        }
      } else {
        displayError(customErrors.customEligibilityQuestionsError ? customErrors.customEligibilityQuestionsError : getContactError(storeContact));
        console.error("Can't create a job. Please review the request to the skedulo template service.");
      }
    }).catch(
      error => {
        displayError(customErrors.customEligibilityQuestionsError ? customErrors.customEligibilityQuestionsError : getContactError(storeContact));
        console.error(error);
      }
    );
    // eslint-disable-next-line
  }, []);

  // Get Available Days Skedulo service call
  useEffect(() => {
    if (
      !regionId ||
      !jobDuration
    ) return;

    if (availableDaysEnable) {
      setLoadingMonthAvailability(true);
      setDisableFilters(true);
      obtainAvailableDates(startDate, endDate);
    } else {
      if (!value) {
        setSelectedDate(startDate);
      }
      setLoadingMonthAvailability(false);
    }
    // eslint-disable-next-line
}, [regionId, jobDuration]);

  // Get Available Schedules Skedulo service call
  useEffect(() => {
    if (
      !regionId ||
      !jobDuration ||
      !selectedDate ||
      (availableDates.length === 0 && availableDaysEnable)
    ) return;

    setLoadingAvailability(true);
    setAvailableTimes([]);
    setDisableDatePicker(true);

    function isValidISODate(dateString) {
      const dateTime = DateTime.fromISO(dateString, { setZone: true });
      return dateTime.isValid;
    }

    const day = !isValidISODate(selectedDate)
      ? selectedDate.startOf("day")
      : DateTime.fromISO(selectedDate).startOf("day").toISODate();
    const time = timePeriod || "all";

    const requestBody = {
      "availableResources": {
        day,
        timePeriod: time,
        timezone,
        duration: jobDuration,
        zipCode: newZipCode,
        jobTags: [skeduloTagName],
        jobType: newProductType,
        latitude: appReference ? (addressCoordinates?.latitude || 0) + 1e-2 : addressCoordinates?.latitude || "",
        longitude: appReference ? (addressCoordinates?.longitude || 0) + 1e-2 : addressCoordinates?.longitude || "",
        ignoreTravelTime: ignoreTravelTime || apptIgnoreTravelTime || false,
        maxTravelTime: maxTravelTime || apptMaxTravelTime || 0,
        suggestedGap: suggestedGap || apptSuggestedGap,
        resultMode: apptResultMode ? apptResultMode : resultMode,
        regionId
      }
    };

    getAvailability(baseUrl, customer, requestBody)
      .then((availabilityResponse) => {
        if (availabilityResponse.length) {
          const { data: { timeslots } } = availabilityResponse[0];
          setLoadingAvailability(false);
          let filteredAvailableTimes = timeslots.filter(
            (item) => item.availableResources.length
          );

          setAvailableTimes(filteredAvailableTimes);

          if (
            newCustomTimeSlotsIsEnable &&
            skeduloInfo?.customTimeSlots?.timeSlots?.length
          ) {
            console.info("setting custom time slot")
            let customTimeSlots = skeduloInfo?.customTimeSlots.timeSlots;

            filteredAvailableTimes = filteredAvailableTimes.filter((slot) => {
              let skeduloStartTime = {
                hour: moment.parseZone(slot.start).hours(),
                minute: moment.parseZone(slot.start).minutes(),
              };

              let skeduloEndTime = {
                hour: moment.parseZone(slot.end).hours(),
                minute: moment.parseZone(slot.end).minutes(),
              };
              // console.log("-----", skeduloInfo?.customTimeSlots.timeSlots);

              return (
                customTimeSlots.findIndex((custom) => {
                  let customStartTime = moment(custom.start, 'HH:mmm a');
                  let customEndTime = moment(custom.end, 'HH:mmm a');

                  if (
                    moment(skeduloStartTime).isSame(customStartTime) ||
                    (moment(skeduloStartTime).isAfter(customStartTime) &&
                      moment(skeduloEndTime).isBefore(customEndTime)) ||
                    moment(skeduloEndTime).isSame(customEndTime)
                  ) {
                    slot['customStart'] = moment
                      .parseZone(slot.start)
                      .set({
                        h: customStartTime.hour(),
                        m: customStartTime.minute(),
                      })
                      .format();

                    slot['customEnd'] = moment
                      .parseZone(slot.end)
                      .set({
                        h: customEndTime.hour(),
                        m: customEndTime.minute(),
                      })
                      .format();
                    return true;
                  }
                }) >= 0
              );
            });

            setAvailableTimes(
              filteredAvailableTimes.filter(
                (slotObj, index) =>
                  index ===
                  filteredAvailableTimes.findIndex(
                    (arrSlot) =>
                      slotObj['customStart'] === arrSlot['customStart'] &&
                      slotObj['customEnd'] === arrSlot['customEnd']
                  )
              )
            );
          }
          setLoadingAvailability(false);
          setDisableDatePicker(false);
        }
        else {
          setLoadingAvailability(false);
          setDisableDatePicker(false);
          displayError(customErrors.customEligibilityQuestionsError ? customErrors.customEligibilityQuestionsError : getContactError(storeContact));
          console.error(`No available resources. There's a problem fetching the Available time slots -> Available time slots service response: ${JSON.stringify(availabilityResponse)}`);
        }
      }).catch(
        error => {
          displayError(customErrors.customEligibilityQuestionsError ? customErrors.customEligibilityQuestionsError : getContactError(storeContact));
          console.error(error);
        }
      );
    // eslint-disable-next-line
  }, [selectedDate, availableDates, timePeriod]);

  function showSpinner() {
    return (
      <div>
        {accountState?.conditionSpecificBehaviors?.translateText &&
                            accountState?.storeInfo?.languageCode ? LANGUAGE_TEXT[accountState?.storeInfo?.languageCode]
                            ?.appointment_loading_text : "Wait one moment while we find available appointments..."}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          marginTop={2}
          width="100%"
        >
          <CircularProgress/>
        </Box>
      </div>
    )
  }

  if (!regionId || !jobDuration) return showSpinner();

  return (
    <ThemeProvider theme={materialTheme}>
      <Grid container>
        <Grid item xs={12} md={6} style={{ pointerEvents: disableDatePicker ? "none" : "" }}>
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={
            accountState?.conditionSpecificBehaviors?.translateText &&
            accountState?.storeInfo?.languageCode === "es_ES"
              ? es
              : enUS
          }>
          <>
            {availableDaysEnable && <EligibilityStaticDateFilter
              handleFilterChange={handleFilterChange}
              value={timePeriod}
              disabled={disableFilters}
              theme={theme}
              LANGUAGE_TEXT = {LANGUAGE_TEXT}
              accountState = {accountState}
            />}
              <DatePicker
                disablePast
                disableToolbar
                minDate={twoDaysFromNow}
                onChange={(date) => DateTime.fromJSDate(date) < twoDaysFromNow
                  ? setSelectedDate(twoDaysFromNow)
                  : setSelectedDate(DateTime.fromJSDate(date))
                }
                onMonthChange={handleMonthChange}
                renderDay={(day, selectedDate, isInCurrentMonth, dayComponent) => getDayElement(day, selectedDate, isInCurrentMonth, dayComponent)}
                shouldDisableDate={(date) => availableDaysEnable && disableDates(date)}
                value={selectedDate}
                variant={"static"}
              />
            </>
          </MuiPickersUtilsProvider>
        </Grid>
        <Grid item xs={12} md={6}>
          <EligibilityTimeList
            handlerInputChange={handlerInputChange}
            items={availableTimes}
            jobDuration={jobDuration}
            productType={productType}
            question={question}
            loadingAvailability={loadingAvailability}
            theme={theme}
            LANGUAGE_TEXT = {LANGUAGE_TEXT}
            accountState = {accountState}
          />
          { loadingMonthAvailability && showSpinner() }
          { loadingAvailability && !loadingMonthAvailability && showSpinner() }
          {!loadingMonthAvailability && noAvailableMonth && !loadingAvailability && <>No times available for the selected month</>}
          {!loadingMonthAvailability && !noAvailableMonth && !loadingAvailability && availableTimes.length === 0 && <>No times available for the selected day</>}
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}

export default withErrorHandler(EligibilityStaticDate);
